import { Icon, CustomTable, ConfirmationPopup, SuccessModal, Loader } from '@ohif/ui';
import React, { useState, useEffect } from 'react';
import { AppDispatch, RootState } from '@custom/store/store';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '../../../ui/src/contextProviders';
import { _routes } from '@custom/Utils/constants/routes';
import { servicesManager } from '../../../app/src/App';
import { deleteCaseCredit } from '@custom/rtk-apis/Setting/creditForCaseToCase/deleteCaseCredit';
import { clearDeleteCaseCreditState } from '@custom/store/slices/Setting/creditForCaseToCase/deleteCaseCreditSlice';
import { _roleType } from '@custom/Utils/constants/appConstants';
import EditCaseToCaseForHospital from './EditCase2CaseCreditForHospital';
import { getHospitalCaseCredits } from '@custom/rtk-apis/Setting/creditForCaseToCase/getHospitalCaseCredit';
import { getCaseCredits } from '@custom/rtk-apis/Setting/creditForCaseToCase/getCaseCredits';
import { clearGetCaseCreditsState } from '@custom/store/slices/Setting/creditForCaseToCase/getCaseCreditsSlice';
import hospitalDetailApi from '@custom/rtk-apis/hospital/hospitalDetails';

interface TeleRadiologyPricesProps {
  creditData?: any[];
  setcreditData?: React.Dispatch<React.SetStateAction<any[]>>;
  isHospitalData?: boolean;
  isCaseToCaseEnabled?: boolean;
  onTableDataUpdate?: (tableData: any[]) => void;
  hospitalId?: string;
  edit?: boolean;
  hospitalData?: any;
}

const CaseToCaseCreditForHospital = ({
  creditData,
  setcreditData,
  isHospitalData,
  isCaseToCaseEnabled,
  onTableDataUpdate,
  hospitalId,
  edit,
  hospitalData
}: TeleRadiologyPricesProps) => {
  const isDarkMode = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { uiNotificationService } = servicesManager?.services;

  const getHospitalCaseCreditResponse = useSelector((state: RootState) => state.getHospitalCaseCreditsResponse);
  const getCaseCreditResponse = useSelector((state: RootState) => state?.getCaseCreditResponse);
  const deleteCaseCreditResponse = useSelector((state: RootState) => state?.deleteCaseCreditResponse);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableData, setTableData] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const hospitalIdForCredit = hospitalData?._id;
  const hospitalDetailId = localStorage.getItem('hospitalId') || sessionStorage.getItem('hospitalId');

  const [columns, setColumns] = useState([
    { key: 'modality', title: 'Modality' },
    { key: 'credit', title: 'Credit' },
  ]);

  useEffect(() => {
    if (hospitalData?.teleCreditServicesForCase2Case?.length > 0) {
      dispatch(getHospitalCaseCredits({ hospitalId: hospitalIdForCredit }));
    } else {
      dispatch(getCaseCredits());
    }
  }, []);

  const isTeleCreditServicesEmpty = !hospitalData?.teleCreditServicesForCase2Case?.length;

  useEffect(() => {
    const updateTableData = () => {
      const creditValue = getCaseCreditResponse?.fullDoc?.creditValue;
      const hospitalCreditValue = getHospitalCaseCreditResponse?.creditValue;

      if (hospitalData?.teleCreditServicesForCase2Case?.length > 0) {
        if (getHospitalCaseCreditResponse?.result && getHospitalCaseCreditResponse?.status === 'SUCCESS') {
          const updatedTableData = (getHospitalCaseCreditResponse.result || []).map(item => ({
            _id: item._id || item.id,
            modality: item.modality,
            credit: item.credit,
            value: item.credit * hospitalCreditValue
          }));
          if (updatedTableData.length > 0) {
            setTableData(updatedTableData);
            if (onTableDataUpdate) onTableDataUpdate(updatedTableData);
            if (setcreditData) setcreditData(updatedTableData);
          } else {
            setTableData([]);
          }
        } else if (getHospitalCaseCreditResponse?.status === 'FAILED') {
          uiNotificationService.show({
            title: 'Error',
            message: getHospitalCaseCreditResponse?.error || 'Failed to fetch hospital case credits',
            type: 'error',
          });
          setTableData([]);
        }
      } else {
        if (getCaseCreditResponse?.result && getCaseCreditResponse?.status === 'SUCCESS') {
          const updatedTableData = (getCaseCreditResponse.result || []).map(item => ({
            _id: item._id || item.id,
            modality: item.modality,
            credit: item.credit,
            value: item.credit * creditValue
          }));
          if (updatedTableData.length > 0) {
            setTableData(updatedTableData);
            if (onTableDataUpdate) onTableDataUpdate(updatedTableData);
            if (setcreditData) setcreditData(updatedTableData);
          } else {
            setTableData([]);
          }
        } else if (getCaseCreditResponse?.status === 'FAILED') {
          uiNotificationService.show({
            title: 'Error',
            message: getCaseCreditResponse?.error || 'Failed to fetch case credits',
            type: 'error',
          });
          setTableData([]);
        }
      }
    };

    updateTableData();
  }, [
    getHospitalCaseCreditResponse?.result,
    getHospitalCaseCreditResponse?.status,
    getCaseCreditResponse?.result,
    getCaseCreditResponse?.status,
    getCaseCreditResponse?.fullDoc?.creditValue,
    hospitalData?.teleCreditServicesForCase2Case?.length
  ]);

  useEffect(() => {
    if (deleteCaseCreditResponse.status === 'SUCCESS' && !deleteCaseCreditResponse.loading) {
      setIsSuccessModal(true);
      setTimeout(() => {
        if (hospitalId) {
          dispatch(getHospitalCaseCredits({ hospitalId: hospitalIdForCredit }));
        }
        dispatch(clearDeleteCaseCreditState());
      }, 500);
    } else if (deleteCaseCreditResponse.status === 'FAILED' && !deleteCaseCreditResponse.loading) {
      setIsSuccessModal(false);
      uiNotificationService.show({
        title: 'Error',
        message: deleteCaseCreditResponse?.error || 'Something went wrong...',
        type: 'error',
      });
      dispatch(clearDeleteCaseCreditState());
    }
  }, [deleteCaseCreditResponse.status, deleteCaseCreditResponse.loading]);

  useEffect(() => {
    const role = sessionStorage.getItem('role') || localStorage.getItem('role');
    if (role === _roleType.superAdmin) {
      setIsSuperAdmin(true);
    }
    setColumns([
      { key: 'modality', title: 'Modality' },
      { key: 'credit', title: 'Credit' },
      ...(edit && isSuperAdmin && !isTeleCreditServicesEmpty ? [{ key: 'actions', title: 'Action' }] : []),
    ]);
  }, [edit, isSuperAdmin]);

  const handleEditClick = (item) => {
    const fullItem = tableData.find((i) => i._id === item._id);
    setEditData(fullItem);
    setShowEditForm(true);
  };

  const actions = [
    {
      label: 'Edit',
      iconName: 'edit',
      onClick: handleEditClick,
    },
  ];

  useEffect(() => {
    if (getCaseCreditResponse?.result && getCaseCreditResponse?.status === 'SUCCESS') {
      const creditValue = getCaseCreditResponse?.fullDoc?.creditValue || 1;
      const updatedTableData = (getCaseCreditResponse.result || []).map(item => ({
        _id: item._id || item.id,
        modality: item.modality,
        credit: item.credit,
        value: item.credit * creditValue
      }));
      setTableData(updatedTableData);
      setTimeout(() => {
        dispatch(clearGetCaseCreditsState());
      }, 500);
    }
  }, [getCaseCreditResponse?.result, getCaseCreditResponse?.fullDoc?.creditValue]);

  const handlePriceUpdate = (updatedItem) => {
    const newTableData = tableData.map((item) =>
      item._id === updatedItem._id ? { ...item, credit: updatedItem.credit } : item
    );
    const creditValue = getCaseCreditResponse?.fullDoc?.creditValue || 1;
    const updatedTableDataWithValue = newTableData.map((item) => ({
      ...item,
      value: item.credit * creditValue
    }));
    setTableData(updatedTableDataWithValue);
    if (onTableDataUpdate) {
      onTableDataUpdate(updatedTableDataWithValue);
    }
    if (setcreditData) {
      setcreditData(updatedTableDataWithValue);
    }
  };

  const handleConfirm = () => {
    if (selectedRow) {
      dispatch(deleteCaseCredit({ id: selectedRow._id }));
    }
    setIsPopupOpen(false);
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
  };

  return (
    <>
      {showEditForm ? (
        <EditCaseToCaseForHospital
          setShowEditForm={setShowEditForm}
          priceModalityData={editData}
          setTableData={setTableData}
          onUpdate={handlePriceUpdate}
          hospitalId={hospitalId}
          hospitalData={hospitalData}
        />
      ) : (
        <div className="tableWrap">
          <div className="tableWrap__head"></div>
          <div className="tableContainer">
            {getHospitalCaseCreditResponse?.loading || deleteCaseCreditResponse?.loading ? (
              <Loader isLoaderOpen={true} />
            ) : tableData.length === 0 ? (
              <div>No Data Found</div>
            ) : (
              <CustomTable
                columns={columns}
                data={tableData}
                actions={edit && isSuperAdmin ? actions : []}
                searchQuery={searchQuery}
                from="creditsforcasetocase"
                userDetails={{}}
                setClickedItem={() => {}}
                clickedItem={null}
                approvedHospitals={[]}
              />
            )}
          </div>
        </div>
      )}
      <ConfirmationPopup
        title=""
        isOpen={isPopupOpen}
        message={`Are you sure you want to delete credits for ${selectedRow?.modality} modality?`}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <SuccessModal
        isSuccessModalOpen={isSuccessModalOpen}
        title="Machine deleted successfully."
        handleCloseModal={() => setIsSuccessModal(false)}
      />
    </>
  );
};

export default CaseToCaseCreditForHospital;
